import { useCallback, useEffect, useState } from 'react';
import { useMediaDevicesContext } from '../../../providers/media-devices-context';
import {
  type AudioIdentity,
  type BrowserAudioFlow,
  safeAudioFlow,
} from '../../../services/audio-flow';
import AvService from '../../../services/av-service';

type UseAvServiceProps = {
  audioFlow?: BrowserAudioFlow;
  onVideoChunk?: (chunk: Blob) => void;
  onPlaybackComplete?: () => void;
};

const useAvService = ({
  audioFlow,
  onVideoChunk,
  onPlaybackComplete,
}: UseAvServiceProps) => {
  const [avService, setAvService] = useState<AvService | null>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [destinationAudioStream, setDestinationAudioStream] =
    useState<MediaStream | null>(null);
  const { selectedCamera, selectedMicrophone, selectedSpeaker } =
    useMediaDevicesContext();

  useEffect(() => {
    const avService = new AvService(audioFlow);
    setAvService(avService);
    setAnalyserNode(avService.getAnalyserNode());
    setDestinationAudioStream(avService.getDestinationStream());

    return () => {
      // needs to be in this exact order since destroy is async
      setAvService(null);
      setAnalyserNode(null);
      setDestinationAudioStream(null);
      avService.destroy();
    };
  }, [audioFlow]);

  useEffect(() => {
    if (!avService || !selectedSpeaker) {
      return;
    }

    avService.setOutputSpeaker(selectedSpeaker).catch((error: unknown) => {
      console.error('Failed to set speaker:', error);
    });
  }, [avService, selectedSpeaker]);

  useEffect(() => {
    if (!avService) {
      return;
    }

    avService.onvideochunk = onVideoChunk;
    avService.onplaybackcomplete = () => {
      setIsPlaying(false);
      onPlaybackComplete?.();
    };

    return () => {
      avService.onvideochunk = undefined;
      avService.onplaybackcomplete = undefined;
    };
  }, [onVideoChunk, onPlaybackComplete, avService]);

  const play = useCallback(
    (data: Readonly<ArrayBuffer>, identity?: AudioIdentity) => {
      if (!avService) {
        safeAudioFlow(() => audioFlow?.playbackFailed());
        return;
      }

      setIsPlaying(true);
      try {
        avService.play(data, identity);
      } catch (error) {
        safeAudioFlow(() => audioFlow?.playbackFailed());
        throw error;
      }
    },
    [avService, audioFlow],
  );

  const interrupt = useCallback(() => {
    setIsPlaying(false);
    safeAudioFlow(() => {
      if (audioFlow) audioFlow.playbackSchedulingFailed = false;
    });
    avService?.interrupt();
  }, [avService, audioFlow]);

  const startRecording = useCallback(() => {
    if (!avService) {
      return;
    }

    avService
      .startRecording({
        chunkDuration: 1000,
        videoDeviceId: selectedCamera,
        audioDeviceId: selectedMicrophone,
        // Safari only seems to support video/mp4 right now
        preferredMimeTypes: [
          'video/webm;codecs=vp8,opus',
          'video/webm',
          'video/mp4',
        ],
      })
      .then((stream) => {
        setVideoStream(stream);
      });
  }, [avService, selectedCamera, selectedMicrophone]);

  const stopRecording = useCallback(() => {
    if (!avService) {
      return Promise.resolve();
    }

    return avService.stopRecording();
  }, [avService]);

  return {
    play,
    interrupt,
    startRecording,
    stopRecording,
    videoStream,
    isPlaying,
    analyserNode,
    destinationAudioStream,
  };
};

export default useAvService;
